import { createContext, useContext, ReactNode } from "react";
import { useIntercomSidePanel } from "@/shared/hooks/useIntercomSidePanel";
import { useAuth } from "@/shared/hooks/useAuth";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useIntercomAttributes } from "@/shared/hooks/useIntercomAttributes";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { useIsLawyer } from "@/shared/hooks/useIsLawyer";
import { useIntakeStatus } from "@/shared/hooks/useIntakeStatus";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";

interface IntercomContextType {
  isOpen: boolean;
  openSidePanel: () => void;
  closeSidePanel: () => void;
  openWithNewMessage: (prePopulatedMessage?: string) => void;
  isInitialized: boolean;
}

const IntercomContext = createContext<IntercomContextType | undefined>(undefined);

export function IntercomProvider({ children }: { children: ReactNode }) {
  const { user, loading: isUserLoading } = useAuth();
  const { user: dbUser, person } = useAppContext();
  const subscription = usePaymentTier(person?.estate_id);
  const customAttributes = useIntercomAttributes({ person, user: dbUser, subscription });
  const { isLawyer } = useIsLawyer();
  const { hasCompletedIntro } = useIntakeStatus();
  const { getCustomization } = useUserCustomizations();
  const hasDismissedTriage = !!getCustomization<boolean>("dismissed_welcome_modal", false);
  const hasReachedDashboard = isLawyer || hasCompletedIntro || hasDismissedTriage;

  const intercomState = useIntercomSidePanel({
    user,
    isUserLoading,
    customAttributes,
    autoBoot: hasReachedDashboard,
  });

  return <IntercomContext.Provider value={intercomState}>{children}</IntercomContext.Provider>;
}

export function useIntercom() {
  const context = useContext(IntercomContext);
  if (context === undefined) {
    throw new Error("useIntercom must be used within an IntercomProvider");
  }
  return context;
}
